import { apiGet, apiPost } from '../ApiClient';

/**
 * Fetch POS Profiles available to the current user.
 * Uses the standard Frappe resource API with filters.
 */
export async function fetchPosProfiles() {
  const res = await apiGet('resource/POS Profile', {
    filters: JSON.stringify([['disabled', '=', 0]]),
    fields: JSON.stringify([
      'name',
      'warehouse',
      'company',
      'currency',
      'customer',
      'selling_price_list',
    ]),
    limit_page_length: 0,
  });
  return res?.data || [];
}

/**
 * Get full POS Profile data with expanded customer groups.
 * Uses the ERPNext point_of_sale whitelisted method.
 */
export async function getPosProfileData(posProfile) {
  const res = await apiPost(
    'method/erpnext.selling.page.point_of_sale.point_of_sale.get_pos_profile_data',
    { pos_profile: posProfile },
  );
  return res?.message;
}

/**
 * Check if the user has an open POS Opening Entry.
 * Returns an array of open vouchers (may be empty).
 */
export async function checkOpeningEntry(user) {
  const res = await apiPost(
    'method/erpnext.selling.page.point_of_sale.point_of_sale.check_opening_entry',
    { user },
  );
  return res?.message || [];
}

/**
 * Create and submit a POS Opening Entry.
 * @param {string} posProfile - POS Profile name
 * @param {string} company - Company name
 * @param {Array} balanceDetails - Array of { mode_of_payment, opening_amount }
 */
export async function createOpeningVoucher(posProfile, company, balanceDetails) {
  const res = await apiPost(
    'method/erpnext.selling.page.point_of_sale.point_of_sale.create_opening_voucher',
    {
      pos_profile: posProfile,
      company,
      balance_details: JSON.stringify(balanceDetails),
    },
  );
  return res?.message;
}

// ---------------------------------------------------------------------------
// Shift closing
// ---------------------------------------------------------------------------

/**
 * Fetch POS Invoices for a given shift period (for closing entry).
 * Returns submitted, unconsolidated invoices for the user+profile.
 */
export async function getClosingInvoices({ start, end, posProfile, user }) {
  const res = await apiPost(
    'method/erpnext.accounts.doctype.pos_closing_entry.pos_closing_entry.get_pos_invoices',
    { start, end, pos_profile: posProfile, user },
  );
  return res?.message || [];
}

/**
 * Fetch a POS Opening Entry document to get its details.
 */
export async function getOpeningEntry(name) {
  const res = await apiGet(`resource/POS Opening Entry/${name}`);
  return res?.data;
}

/**
 * Create and submit a POS Closing Entry.
 *
 * @param {Object} params
 * @param {string} params.openingEntryName - POS Opening Entry name
 * @param {Object} params.openingEntry - Full opening entry document
 * @param {Array} params.invoices - POS Invoices from getClosingInvoices()
 * @param {Array} params.paymentReconciliation - [{ mode_of_payment, opening_amount, expected_amount, closing_amount }]
 * @returns {Promise<Object>} Created + submitted closing entry
 */
export async function createClosingEntry({
  openingEntry,
  invoices,
  paymentReconciliation,
}) {
  const now = new Date();

  const payload = {
    doctype: 'POS Closing Entry',
    pos_opening_entry: openingEntry.name,
    period_start_date: openingEntry.period_start_date,
    period_end_date: now.toISOString().replace('T', ' ').split('.')[0],
    posting_date: now.toISOString().split('T')[0],
    posting_time: now.toTimeString().split(' ')[0],
    company: openingEntry.company,
    pos_profile: openingEntry.pos_profile,
    user: openingEntry.user,
    pos_transactions: invoices.map((inv) => ({
      pos_invoice: inv.name,
      posting_date: inv.posting_date,
      customer: inv.customer,
      grand_total: inv.grand_total,
      is_return: inv.is_return || 0,
    })),
    payment_reconciliation: paymentReconciliation,
    grand_total: invoices.reduce((s, i) => s + (i.is_return ? 0 : (i.grand_total || 0)), 0),
    net_total: invoices.reduce((s, i) => s + (i.is_return ? 0 : (i.net_total || 0)), 0),
    total_quantity: invoices.reduce((s, i) => s + (i.total_qty || 0), 0),
  };

  // Create the document
  const createRes = await apiPost('resource/POS Closing Entry', payload);
  const created = createRes?.data;

  // Submit it (pass full doc to avoid TimestampMismatchError)
  await apiPost('method/frappe.client.submit', {
    doc: JSON.stringify(created),
  });

  return created;
}
